/**
 * ToolsPanel — the right-rail tools drawer.
 *
 * Composes everything: PushButton, ToolsPanelGroup, ToolsPanelButton,
 * the icon library, the export helpers, and the 6 modals.
 *
 * Privileged users (Developer / Director / AD / Lieutenant / Sergeant)
 * see the Navigate group (Daily Posting, Manage Roster, Manage Users,
 * Search History, Audit Log, Settings, Pending Approvals).
 *
 * Everyone sees the Actions group (Import, Print, Export Word,
 * Archive Shift, NA Log).
 */
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { workerPost } from '../../api/worker'
import { useAuthStore } from '../../stores/auth'
import PushButton from './PushButton'
import ToolsPanelGroup from './ToolsPanelGroup'
import ToolsPanelButton from './ToolsPanelButton'
import {
  ICON_IMPORT, ICON_PRINT, ICON_DOWNLOAD, ICON_ARCHIVE, ICON_ALERT,
  ICON_POSTING, ICON_USERS, ICON_USER_X, ICON_SEARCH, ICON_AUDIT,
  ICON_SETTINGS, ICON_CLOCK,
} from './icons'
import { downloadShiftDoc, printShiftDoc } from './exportShiftHtml'
import NaLogModal      from './modals/NaLogModal'
import HistoryModal    from './modals/HistoryModal'
import AuditModal      from './modals/AuditModal'
import UsersModal      from './modals/UsersModal'
import SettingsModal   from './modals/SettingsModal'
import PendingModal    from './modals/PendingModal'

interface ToolsPanelProps {
  open: boolean
  isPriv: boolean
  panelWidth: number
  headerHeight: number
  onNavigate: (sectionId: string) => void
  onClose: () => void
  /** Optional override for the Daily Posting button. When provided, fires
   *  this callback instead of navigating — used by DesktopShell to open
   *  the PostingModal in place. */
  onPostingClick?: () => void
}

function readNaCount(): number {
  try {
    return (JSON.parse(localStorage.getItem('fom_na_log') ?? '[]') as unknown[]).length
  } catch {
    return 0
  }
}

export default function ToolsPanel({
  open,
  isPriv,
  panelWidth,
  headerHeight,
  onNavigate,
  onClose,
  onPostingClick,
}: ToolsPanelProps) {
  const navigate = useNavigate()
  const session  = useAuthStore(s => s.session)
  const fileRef  = useRef<HTMLInputElement>(null)

  const [showNa,       setShowNa]       = useState(false)
  const [showHistory,  setShowHistory]  = useState(false)
  const [showAudit,    setShowAudit]    = useState(false)
  const [showUsers,    setShowUsers]    = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showPending,  setShowPending]  = useState(false)

  const naCount = readNaCount()

  async function doArchive() {
    if (!confirm('Archive current shift?')) return
    try {
      const res = await workerPost<{ success: boolean; message?: string; error?: string }>({
        authAction: 'archiveShift',
        token: session?.token ?? '',
      })
      alert(
        res.success
          ? 'Shift archived! ' + (res.message ?? '')
          : (res.error ?? 'Archive failed'),
      )
    } catch {
      alert('Archive failed: network error')
    }
  }

  function handleImportClick() {
    fileRef.current?.click()
  }

  function handleFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) {
      alert(`Import from Word: "${f.name}"\n\nUse cloud sync to share data between devices.`)
    }
    e.target.value = ''
  }

  return (
    <>
      {showNa       && <NaLogModal      onClose={() => setShowNa(false)} />}
      {showHistory  && <HistoryModal    onClose={() => setShowHistory(false)} />}
      {showAudit    && <AuditModal      onClose={() => setShowAudit(false)} />}
      {showUsers    && <UsersModal      onClose={() => setShowUsers(false)} />}
      {showSettings && <SettingsModal   onClose={() => setShowSettings(false)} />}
      {showPending  && <PendingModal    onClose={() => setShowPending(false)} />}

      <input
        ref={fileRef}
        type="file"
        accept=".docx"
        style={{ display: 'none' }}
        onChange={handleFileChosen}
      />

      <div
        style={{
          position: 'fixed',
          top: headerHeight,
          right: 0,
          bottom: 0,
          width: panelWidth,
          background: 'var(--bg-surface)',
          borderLeft: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflowY: 'auto',
          overflowX: 'hidden',
          transform: open ? 'translateX(0)' : `translateX(${panelWidth}px)`,
          transition: 'transform 0.25s ease',
          zIndex: 900,
        }}
      >
        <div
          style={{
            padding: '14px 0 10px',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <PushButton />
        </div>

        {isPriv && (
          <ToolsPanelGroup label="Navigate" color="#f59e0b">
            <ToolsPanelButton
              gradient="#f59e0b,#d97706"
              shadow="245,158,11"
              icon={ICON_POSTING}
              label="Daily Posting"
              onClick={() => {
                if (onPostingClick) onPostingClick()
                else navigate('/posting')
                onClose()
              }}
            />
            <ToolsPanelButton
              gradient="#059669,#047857"
              shadow="5,150,105"
              icon={ICON_USERS}
              label="Manage Roster"
              onClick={() => { onNavigate('sec-roster'); onClose() }}
            />
            <ToolsPanelButton
              gradient="#e11d48,#be123c"
              shadow="225,29,72"
              icon={ICON_USER_X}
              label="Manage Users"
              onClick={() => setShowUsers(true)}
            />
            <ToolsPanelButton
              gradient="#0d9488,#0f766e"
              shadow="13,148,136"
              icon={ICON_SEARCH}
              label="Search History"
              onClick={() => setShowHistory(true)}
            />
            <ToolsPanelButton
              gradient="#8b5cf6,#7c3aed"
              shadow="139,92,246"
              icon={ICON_AUDIT}
              label="Audit Log"
              onClick={() => setShowAudit(true)}
            />
            {isPriv && (
              <ToolsPanelButton
                gradient="#dc2626,#b91c1c"
                shadow="220,38,38"
                icon={ICON_CLOCK}
                label="Pending Approvals"
                onClick={() => setShowPending(true)}
              />
            )}
          </ToolsPanelGroup>
        )}

        <ToolsPanelGroup label="Actions" color="#6366f1">
          <ToolsPanelButton
            gradient="#6366f1,#4f46e5"
            shadow="99,102,241"
            icon={ICON_IMPORT}
            label="Import Word"
            onClick={handleImportClick}
          />
          <ToolsPanelButton
            gradient="#7c3aed,#6d28d9"
            shadow="124,58,237"
            icon={ICON_PRINT}
            label="Print"
            onClick={printShiftDoc}
          />
          <ToolsPanelButton
            gradient="#2563eb,#1d4ed8"
            shadow="37,99,235"
            icon={ICON_DOWNLOAD}
            label="Export Word"
            onClick={downloadShiftDoc}
          />
          {isPriv && (
            <ToolsPanelButton
              gradient="#0891b2,#0e7490"
              shadow="8,145,178"
              icon={ICON_ARCHIVE}
              label="Archive Shift"
              onClick={doArchive}
            />
          )}
          <ToolsPanelButton
            gradient={naCount > 0 ? '#dc2626,#b91c1c' : '#4b5563,#374151'}
            shadow={naCount > 0 ? '220,38,38' : '75,85,99'}
            icon={ICON_ALERT}
            label="NA Log"
            badge={naCount}
            onClick={() => setShowNa(true)}
          />
          <ToolsPanelButton
            gradient="#475569,#334155"
            shadow="71,85,105"
            icon={ICON_SETTINGS}
            label="Settings"
            onClick={() => setShowSettings(true)}
          />
        </ToolsPanelGroup>
      </div>
    </>
  )
}
